import { TrendingUp, TrendingDown, Minus, GitCompareArrows, CalendarRange, CalendarDays } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatVNDShort, formatPercent } from '@/lib/format';
import type { AutoCompare, ComparePair } from '@/lib/api';

function delta(current: number, prev: number): number {
  if (prev <= 0) return 0;
  return ((current - prev) / prev) * 100;
}

function MiniDelta({ pct }: { pct: number }) {
  if (Math.abs(pct) < 0.1) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[11px] text-muted-foreground">
        <Minus className="h-3 w-3" />
        ±0%
      </span>
    );
  }
  const Icon = pct > 0 ? TrendingUp : TrendingDown;
  const colorClass = pct > 0 ? 'text-cat-graphic' : 'text-destructive';
  return (
    <span className={cn('inline-flex items-center gap-0.5 text-[11px] font-medium', colorClass)}>
      <Icon className="h-3 w-3" strokeWidth={2.5} />
      {pct > 0 ? '+' : ''}
      {pct.toFixed(1)}%
    </span>
  );
}

interface ComparisonRowProps {
  label: string;
  currentValue: string;
  prevValue: string;
  deltaPct: number;
}

function ComparisonRow({ label, currentValue, prevValue, deltaPct }: ComparisonRowProps) {
  return (
    <div className="grid grid-cols-3 items-center gap-2 border-b py-2 last:border-b-0">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="flex items-baseline gap-2">
        <span className="text-base font-bold tabular-nums">{currentValue}</span>
        <span className="text-xs tabular-nums text-muted-foreground">vs {prevValue}</span>
      </div>
      <div className="text-right">
        <MiniDelta pct={deltaPct} />
      </div>
    </div>
  );
}

function CompareCard({
  pair,
  title,
  icon: Icon,
}: {
  pair: ComparePair;
  title: string;
  icon: typeof CalendarRange;
}) {
  const c = pair.current.totals;
  const p = pair.previous.totals;
  return (
    <div className="rounded-lg border bg-card p-4 shadow-sm print:rounded-md print:shadow-none print-no-break">
      <div className="mb-2 flex items-center gap-2">
        <Icon className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-semibold">{title}</h3>
        <span className="ml-auto text-[10px] text-muted-foreground">
          {pair.current.period.label} vs {pair.previous.period.label}
        </span>
      </div>
      <div className="rounded-md border bg-muted/20">
        <div className="px-3">
          <ComparisonRow
            label="Doanh thu"
            currentValue={formatVNDShort(c.revenue_vnd)}
            prevValue={formatVNDShort(p.revenue_vnd)}
            deltaPct={delta(c.revenue_vnd, p.revenue_vnd)}
          />
          <ComparisonRow
            label="Sản lượng"
            currentValue={c.units_sold.toLocaleString('vi-VN')}
            prevValue={p.units_sold.toLocaleString('vi-VN')}
            deltaPct={delta(c.units_sold, p.units_sold)}
          />
          <ComparisonRow
            label="Margin"
            currentValue={formatPercent(c.margin_pct, 1)}
            prevValue={formatPercent(p.margin_pct, 1)}
            deltaPct={delta(c.margin_pct, p.margin_pct)}
          />
          <ComparisonRow
            label="Thị phần"
            currentValue={formatPercent(c.market_share_revenue_pct, 1)}
            prevValue={formatPercent(p.market_share_revenue_pct, 1)}
            deltaPct={delta(c.market_share_revenue_pct, p.market_share_revenue_pct)}
          />
        </div>
      </div>
    </div>
  );
}

export interface ReportAutoCompareProps {
  data: AutoCompare;
}

export function ReportAutoCompare({ data }: ReportAutoCompareProps) {
  if (!data || (!data.qoq && !data.mom)) return null;

  return (
    <section className="space-y-3 print-no-break">
      <div className="flex items-center gap-2">
        <GitCompareArrows className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          So sánh kỳ gần nhất
        </h2>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {data.qoq && <CompareCard pair={data.qoq} title="Quý hiện tại vs Quý trước" icon={CalendarRange} />}
        {data.mom && <CompareCard pair={data.mom} title="Tháng hiện tại vs Tháng trước" icon={CalendarDays} />}
      </div>
    </section>
  );
}
